import { ThemeEvents } from '@theme/events';

/**
 * Keeps the sticky bar's quantity stepper and the buy box's in step
 * (Figma 896:3146, `snippets/admira-sticky-add-to-cart.liquid`).
 *
 * The sticky bar adds nothing to the cart itself: Horizon's
 * `sticky-add-to-cart.js` clicks the buy box's own add-to-cart button, and the
 * buy box's form submits the buy box's quantity. So the number in the bar has
 * to *be* that quantity. Both steppers are Horizon's `quantity-selector`, and
 * both already announce every change with `QuantitySelectorUpdateEvent`; this
 * module listens for it and writes the value across with the component's own
 * `setValue`, which does not re-announce — so there is no loop.
 *
 * One more case: when the variant changes, Horizon morphs the bar from the
 * server's markup, which resets its stepper to the default. The bar is hidden
 * while the buy box is on screen, which is the only place a variant can be
 * chosen, so the bar re-reads the buy box whenever it comes into view.
 */

/**
 * @typedef {HTMLElement & {
 *   getValue: () => string,
 *   setValue: (value: string) => void,
 *   updateButtonStates?: () => void
 * }} QuantitySelector
 */

/**
 * @param {Element} sticky - The `sticky-add-to-cart` element.
 * @returns {QuantitySelector | null}
 */
function buyBoxSelector(sticky) {
  const productId = sticky.getAttribute('data-product-id');
  const scope = sticky.closest('.shopify-section') ?? document;
  return /** @type {QuantitySelector | null} */ (
    scope.querySelector(`product-form-component[data-product-id="${productId}"] quantity-selector-component`)
  );
}

/**
 * @param {Element} sticky
 * @returns {QuantitySelector | null}
 */
function barSelector(sticky) {
  return /** @type {QuantitySelector | null} */ (sticky.querySelector('quantity-selector-component'));
}

/**
 * @param {QuantitySelector | null} selector
 * @param {string} value
 */
function write(selector, value) {
  if (!selector || typeof selector.setValue !== 'function' || selector.getValue() === value) return;
  selector.setValue(value);
  selector.updateButtonStates?.();
}

document.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
  const detail = /** @type {{ quantity?: number, cartLine?: number }} */ (/** @type {any} */ (event).detail ?? {});
  if (detail.cartLine || !(event.target instanceof Element) || detail.quantity == null) return;

  const value = String(detail.quantity);
  const inBar = event.target.closest('[data-admira-sticky]');

  if (inBar) {
    write(buyBoxSelector(inBar), value);
    return;
  }

  const form = event.target.closest('product-form-component');
  const productId = form?.getAttribute('data-product-id');
  if (!productId) return;

  for (const sticky of document.querySelectorAll(`[data-admira-sticky][data-product-id="${productId}"]`)) {
    write(barSelector(sticky), value);
  }
});

const stuckObserver = new MutationObserver((records) => {
  for (const record of records) {
    const bar = record.target;
    if (!(bar instanceof HTMLElement) || bar.dataset.stuck !== 'true') continue;

    const sticky = bar.closest('[data-admira-sticky]');
    const source = sticky ? buyBoxSelector(sticky) : null;
    if (sticky && source) write(barSelector(sticky), source.getValue());
  }
});

for (const bar of document.querySelectorAll('[data-admira-sticky] [ref="stickyBar"]')) {
  stuckObserver.observe(bar, { attributes: true, attributeFilter: ['data-stuck'] });
}
